/**
 * Bot navigation data: a hand-placed waypoint graph per arena map, kept next
 * to the brush lists it describes. This is the Quake III "area + reachability"
 * idea at Callout's scale — typed edges (walk / jump / drop) instead of a
 * navmesh, because the maps are tiny, hand-authored boxes and the valuable
 * part (vertical links, cover spots) is hand-authored under any approach.
 *
 * Every edge is validated by replaying it through the deterministic sim in
 * `apps/server/src/nav.test.ts` — the graph cannot silently rot when movement
 * constants or brushes change. If you edit a map, run that test.
 *
 * Positions are feet positions on the floor; `y` matters on elevated nodes so
 * the router never confuses a catwalk with the ground under it.
 */

export const NAV_PRECISE = 1 << 0; // no corner-cutting into or out of this node (catwalks, stairs)
export const NAV_COVER = 1 << 1; // adjacent to cover worth retreating to
export const NAV_SNIPER = 1 << 2; // long sightlines — a hold-position spot

export interface NavNodeDef {
  x: number;
  y: number;
  z: number;
  /** "Anywhere within" arrival radius — kills robotic node-to-node lines. */
  radius: number;
  flags: number;
}

export interface NavEdgeDef {
  a: number;
  b: number;
  /**
   * walk: flat or auto-step terrain, bidirectional by default.
   * jump: a >step-height rise from a to b — press Jump at the lip; one-way.
   * drop: walk off a ledge from a down to b; one-way.
   */
  type: 'walk' | 'jump' | 'drop';
  oneWay?: boolean;
}

export interface NavData {
  nodes: NavNodeDef[];
  edges: NavEdgeDef[];
}

const n = (x: number, y: number, z: number, radius: number, flags = 0): NavNodeDef => ({
  x,
  y,
  z,
  radius,
  flags,
});

const walk = (a: number, b: number): NavEdgeDef => ({ a, b, type: 'walk' });
const jump = (a: number, b: number): NavEdgeDef => ({ a, b, type: 'jump', oneWay: true });
const drop = (a: number, b: number): NavEdgeDef => ({ a, b, type: 'drop', oneWay: true });

// ---------------------------------------------------------------------------
// Warehouse — ground ring around the mid tower, two catwalks entered by
// stairs at opposite ends and exited by a drop at the other.
// ---------------------------------------------------------------------------

const WAREHOUSE_NAV: NavData = {
  nodes: [
    /* 0 blueSpawn  */ n(-31, 0, -31, 3),
    /* 1 redSpawn   */ n(31, 0, 31, 3),
    /* 2 wLaneN     */ n(-31, 0, -8, 2.5),
    /* 3 eLaneS     */ n(31, 0, 8, 2.5),
    /* 4 wLaneS     */ n(-32, 0, 12, 2.5),
    /* 5 eLaneN     */ n(32, 0, -12, 2.5),
    /* 6 nMid       */ n(0, 0, -21, 2.5, NAV_COVER),
    /* 7 sMid       */ n(0, 0, 21, 2.5, NAV_COVER),
    /* 8 nwMid      */ n(-12, 0, -10, 2),
    /* 9 seMid      */ n(12, 0, 10, 2),
    /* 10 wFlank    */ n(-10, 0, 0, 1.8),
    /* 11 eFlank    */ n(10, 0, 0, 1.8),
    /* 12 swCrate   */ n(-12, 0, 17, 1.8, NAV_COVER),
    /* 13 neCrate   */ n(12, 0, -17, 1.8, NAV_COVER),
    /* 14 wCatLand  */ n(-22, 0, 22, 2),
    /* 15 eCatLand  */ n(22, 0, -22, 2),
    /* 16 wStairFoot*/ n(-22, 0, -30, 1.6),
    /* 17 wStairTop */ n(-22, 3.6, -18, 1.4, NAV_PRECISE),
    /* 18 wCatMid   */ n(-22, 3.25, 0, 2, NAV_PRECISE | NAV_SNIPER),
    /* 19 wCatS     */ n(-22, 3.25, 15.5, 1.6, NAV_PRECISE),
    /* 20 eStairFoot*/ n(22, 0, 30, 1.6),
    /* 21 eStairTop */ n(22, 3.6, 18, 1.4, NAV_PRECISE),
    /* 22 eCatMid   */ n(22, 3.25, 0, 2, NAV_PRECISE | NAV_SNIPER),
    /* 23 eCatN     */ n(22, 3.25, -15.5, 1.6, NAV_PRECISE),
  ],
  edges: [
    walk(0, 16),
    walk(0, 2),
    walk(0, 8),
    walk(2, 8),
    walk(2, 4),
    walk(4, 12),
    walk(4, 14),
    walk(4, 7),
    walk(14, 7),
    walk(12, 7),
    walk(8, 10),
    walk(8, 6),
    walk(10, 7),
    walk(6, 13),
    walk(13, 5),
    walk(5, 6),
    walk(5, 3),
    walk(3, 1),
    walk(1, 20),
    walk(1, 9),
    walk(9, 11),
    walk(9, 7),
    walk(11, 6),
    walk(15, 5),
    walk(15, 6),
    walk(15, 13),
    // West catwalk: stairs up at the north end, drop off the south end.
    walk(16, 17),
    walk(17, 18),
    walk(18, 19),
    drop(19, 14),
    // East catwalk mirrors it.
    walk(20, 21),
    walk(21, 22),
    walk(22, 23),
    drop(23, 15),
  ],
};

// ---------------------------------------------------------------------------
// Foundry — a ground ring around the smelter and the full gantry ring above,
// entered by the two corner stair flights, exited by drops on every side.
// ---------------------------------------------------------------------------

const FOUNDRY_NAV: NavData = {
  nodes: [
    /* 0 blueSpawn  */ n(-21, 0, 5, 2.5),
    /* 1 redSpawn   */ n(21, 0, -5, 2.5),
    /* 2 wSouth     */ n(-19, 0, 17, 1.8),
    /* 3 eNorth     */ n(19, 0, -17, 1.8),
    /* 4 nMid       */ n(0, 0, -13, 2, NAV_COVER),
    /* 5 sMid       */ n(0, 0, 13, 2, NAV_COVER),
    /* 6 wLane      */ n(-18, 0, -9, 1.8),
    /* 7 eLane      */ n(18, 0, 9, 1.8),
    /* 8 wInner     */ n(-8, 0, 8, 1.5, NAV_COVER),
    /* 9 eInner     */ n(8, 0, -8, 1.5, NAV_COVER),
    /* 10 wSS       */ n(-12, 0, 19, 1.8),
    /* 11 eNN       */ n(12, 0, -19, 1.8),
    /* 12 wDrop     */ n(-12.5, 0, 0, 1.5),
    /* 13 eDrop     */ n(12.5, 0, 0, 1.5),
    /* 14 nStrip    */ n(20, 0, -23, 1.5),
    /* 15 sStrip    */ n(-20, 0, 23, 1.5),
    /* 16 wFoot     */ n(-24.5, 0, -18.5, 1.2),
    /* 17 wStairBase*/ n(-24.6, 0.9, -23.25, 1, NAV_PRECISE),
    /* 18 wLanding  */ n(-11.3, 6.3, -23, 1.3, NAV_PRECISE),
    /* 19 gNW       */ n(-18, 6.25, -18, 1.6, NAV_PRECISE),
    /* 20 gNE       */ n(18, 6.25, -18, 1.6, NAV_PRECISE | NAV_SNIPER),
    /* 21 gE        */ n(18, 6.25, 0, 1.6, NAV_PRECISE),
    /* 22 gSE       */ n(18, 6.25, 18, 1.6, NAV_PRECISE),
    /* 23 gSW       */ n(-18, 6.25, 18, 1.6, NAV_PRECISE | NAV_SNIPER),
    /* 24 gW        */ n(-18, 6.25, 0, 1.6, NAV_PRECISE),
    /* 25 eFoot     */ n(24.5, 0, 18.5, 1.2),
    /* 26 eStairBase*/ n(24.6, 0.9, 23.25, 1, NAV_PRECISE),
    /* 27 eLanding  */ n(11.3, 6.3, 23, 1.3, NAV_PRECISE),
    // The inner railings cross the full gantry at z = ±15 — the ring is only
    // continuous if you hop them. One node per crossing, jumped from both sides.
    /* 28 railNW    */ n(-18, 6.25, -15, 1.2, NAV_PRECISE),
    /* 29 railNE    */ n(18, 6.25, -15, 1.2, NAV_PRECISE),
    /* 30 railSE    */ n(18, 6.25, 15, 1.2, NAV_PRECISE),
    /* 31 railSW    */ n(-18, 6.25, 15, 1.2, NAV_PRECISE),
  ],
  edges: [
    walk(0, 6),
    walk(0, 2),
    walk(0, 8),
    walk(0, 12),
    walk(2, 10),
    walk(10, 5),
    walk(5, 8),
    walk(5, 7),
    walk(7, 1),
    walk(7, 25),
    walk(1, 3),
    walk(1, 13),
    walk(1, 9),
    walk(3, 11),
    walk(3, 14),
    walk(11, 4),
    walk(4, 9),
    walk(4, 6),
    walk(6, 16),
    walk(12, 8),
    walk(13, 9),
    walk(15, 2),
    // West stairs: the low steps hug the wall too tightly to walk onto, so
    // entry is a hop onto the flight; leaving down the steps is a walk-off.
    jump(16, 17),
    drop(17, 16),
    walk(17, 18),
    walk(18, 19),
    // The ring, hopping the cross-rails at z = ±15.
    walk(19, 20),
    jump(20, 29),
    jump(29, 20),
    jump(21, 29),
    jump(29, 21),
    jump(21, 30),
    jump(30, 21),
    jump(22, 30),
    jump(30, 22),
    walk(22, 23),
    jump(23, 31),
    jump(31, 23),
    jump(24, 31),
    jump(31, 24),
    jump(24, 28),
    jump(28, 24),
    jump(19, 28),
    jump(28, 19),
    // East stairs.
    jump(25, 26),
    drop(26, 25),
    walk(26, 27),
    walk(27, 22),
    // Gantry exits: inward drops on the railless W/E sides, outer-strip drops
    // on the railed N/S sides.
    drop(24, 12),
    drop(21, 13),
    drop(20, 14),
    drop(23, 15),
  ],
};

// ---------------------------------------------------------------------------
// Sandstone — a wide ground ring through the pillar field, the raised plaza
// entered by jumping the two ramps, and drop-only spawn bastions.
// ---------------------------------------------------------------------------

const SANDSTONE_NAV: NavData = {
  nodes: [
    /* 0 blueSpawn  */ n(-42, 0, 0, 3),
    /* 1 redSpawn   */ n(42, 0, 0, 3),
    /* 2 wMid       */ n(-20, 0, -4, 2),
    /* 3 eMid       */ n(20, 0, 4, 2),
    /* 4 nMid       */ n(0, 0, -20, 2.5),
    /* 5 sMid       */ n(0, 0, 20, 2.5),
    /* 6 nwCross    */ n(-24, 0, -13, 2),
    /* 7 seCross    */ n(24, 0, 13, 2),
    /* 8 neCross    */ n(13, 0, -25, 1.8),
    /* 9 swCross    */ n(-13, 0, 25, 1.8),
    /* 10 nwCorner  */ n(-29, 0, -29, 2),
    /* 11 seCorner  */ n(29, 0, 29, 2),
    /* 12 swCorner  */ n(-29, 0, 31, 2),
    /* 13 neCorner  */ n(29, 0, -31, 2),
    /* 14 dNW       */ n(-16, 0, -12, 1.5),
    /* 15 dSE       */ n(16, 0, 12, 1.5),
    /* 16 dNE       */ n(13, 0, -16, 1.5),
    /* 17 dSW       */ n(-13, 0, 16, 1.5),
    /* 18 rampN     */ n(0, 1, -14, 1.6, NAV_PRECISE),
    /* 19 rampS     */ n(0, 1, 14, 1.6, NAV_PRECISE),
    /* 20 pNW       */ n(-8, 2, -8, 1.5, NAV_PRECISE),
    /* 21 pNE       */ n(8, 2, -8, 1.5, NAV_PRECISE),
    /* 22 pSE       */ n(8, 2, 8, 1.5, NAV_PRECISE),
    /* 23 pSW       */ n(-8, 2, 8, 1.5, NAV_PRECISE),
    /* 24 bastionNW */ n(-38, 4, -38, 2.5, NAV_PRECISE | NAV_SNIPER),
    /* 25 bastionSE */ n(38, 4, 38, 2.5, NAV_PRECISE | NAV_SNIPER),
    // Plaza lips just past the 1 m rise from each ramp: jump edges must end
    // right after their obstacle, or the jump fires nowhere near the lip.
    /* 26 pLipN     */ n(0, 2, -10.5, 1.2, NAV_PRECISE),
    /* 27 pLipS     */ n(0, 2, 10.5, 1.2, NAV_PRECISE),
  ],
  edges: [
    walk(0, 2),
    walk(0, 6),
    walk(0, 9),
    walk(2, 6),
    walk(2, 4),
    walk(6, 4),
    walk(6, 10),
    walk(4, 8),
    walk(8, 13),
    walk(13, 1),
    walk(1, 3),
    walk(1, 7),
    walk(3, 7),
    walk(7, 11),
    walk(11, 1),
    walk(5, 9),
    walk(9, 12),
    walk(12, 0),
    walk(5, 7),
    walk(14, 6),
    walk(14, 4),
    walk(16, 8),
    walk(17, 9),
    walk(15, 7),
    // The plaza: jump up the ramps, over the lip, ring around the cap, drop
    // off any side.
    jump(4, 18),
    drop(18, 4),
    jump(18, 26),
    walk(26, 20),
    walk(26, 21),
    drop(20, 18),
    drop(21, 18),
    jump(5, 19),
    drop(19, 5),
    jump(19, 27),
    walk(27, 22),
    walk(27, 23),
    drop(22, 19),
    drop(23, 19),
    walk(20, 21),
    walk(21, 22),
    walk(22, 23),
    walk(23, 20),
    drop(20, 14),
    drop(21, 16),
    drop(22, 15),
    drop(23, 17),
    // Spawn bastions are drop-only: you start up there, you leave once.
    drop(24, 10),
    drop(25, 11),
  ],
};

export const NAV_GRAPHS: Record<string, NavData> = {
  warehouse: WAREHOUSE_NAV,
  foundry: FOUNDRY_NAV,
  sandstone: SANDSTONE_NAV,
};

export const navDataFor = (mapId: string): NavData | null =>
  NAV_GRAPHS[mapId] ?? null;
